
import { defineComponent } from 'vue';
import {
  formatWithLocale,
  parseISO,
  guessTimezone,
  format,
  DATE_TIME_FORMATS
} from '@white-label-helper/date-utilities';
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { TimeslotFormData, LoungeFormWithFlightDetails } from '@white-label-types/component';
import SearchTimeslotEditForm from '../search-timeslot-edit-form/search-timeslot-edit-form.vue';
import { isPusherErrorWithResponse, searchPusherHelper } from '@white-label-helper/pusher-helper-search';
import { extractFlights, timeslotProductFilter } from 'ecommerce/shared-helpers';
import { datadogRum } from '@datadog/browser-rum';
import { FlightInfo } from '@white-label-types/search-box';
import { APP_HERO_PRODUCTS, PRODUCT_TYPES } from '@white-label-configuration/constants';

export default defineComponent({
  name: 'SearchTimeslotWidget',
  components: {
    IconButton: () => import('@white-label-icon/icon-button'),
    IconPencil: () => import('@white-label-icon/icon-pencil'),
    SearchTimeslotEditForm,
  },
  props: {
    formData: {
      type: Object as () => TimeslotFormData,
      required: true,
    },
    maxPassengers: {
      type: Number,
      default: 6,
    },
    updatingSearch: {
      type: Boolean,
      default: false,
    },
    editMode: {
      type: Boolean,
      required: true,
    },
    customErrorMessages: {
      type: Object,
      default: () => ({}),
    },
    apiRequestRunning: {
      type: Boolean,
      default: false,
    },
    flightNumber: {
      type: String,
      default: '',
    },
  },
  emits: [
    'update:editMode',
    'update:updatingSearch',
    'update:newSearch',
    'update:timeslotResults',
    'update:formData',
    'updatePassengers',
    'mounted',
    'created',
  ],
  data() {
    return {
      showDesktopBackdrop: false,
      selectedFlight: {} as FlightInfo,
      $vueInstance: null as Vue | null,
      flights: [] as any[],
      flightsRequestStatus: 'empty' as 'empty'|'loading'|'success'|'error',
      localFormData: {} as TimeslotFormData,
      lastFetchedFlightQuery: undefined as (TimeslotFormData | undefined),
      lastFetchedProductQuery: undefined as (TimeslotFormData | undefined),
    };
  },
  computed: {
    partnerTimeZone(): string {
      return getAppVariable('poi.timezone') || guessTimezone();
    },
    localEditMode: {
      get(): boolean {
        return this.editMode;
      },
      set(val: boolean) {
        this.$emit('update:editMode', val);
      },
    },
    formattedDepartureDate(): string {
      return this.localFormData.departureDate
        ? formatWithLocale(parseISO(this.localFormData.departureDate), 'dd MMM', 'en-GB')
        : '';
    },
    showEditForm(): boolean {
      return this.isDesktopResolution || this.localEditMode;
    },
    isScrollLocked(): boolean {
      return (this.isMobileResolution || this.isTabletResolution) && this.showEditForm;
    },
    queryParams() {
      return this.$route.query
    },
  },
  watch: {
    formData: {
      handler(newFormData: TimeslotFormData) {
        this.localFormData = { departureDate: '', ...newFormData, passengers: Number(newFormData.passengers) };
      },
      immediate: true,
      deep: true,
    },
    localFormData: {
      handler(newFormData: TimeslotFormData) {
        if (this.formData.passengers !== newFormData.passengers) {
          this.$emit('updatePassengers', newFormData.passengers);
        }
      },
      immediate: true,
      deep: true,
    },
    queryParams(query) {
      if (query === undefined || !query.departureDate || !query.flightNumber) return;

      this.updateLocalFormData({
        departureDate: query.departureDate as string,
        passengers: query.passengers ? parseInt(query.passengers as string, 10) : 1,
        flightNumber: query.flightNumber as string,
      });

      this.getFlights(this.localFormData)
        .then((flights => {
          if (this.localFormData.flightNumber === '') return;
          const foundFlight = flights.find(
            (flight) => flight.flightNumber === this.localFormData.flightNumber
          );
          if (!foundFlight) {
            this.updateSearch({
              ...this.localFormData,
              flightNumber: "",
            });
            return;
          }
          this.selectedFlight = foundFlight;
          this.getProducts();
        }));
    },
  },
  mounted() {
    this.$vueInstance = this.$root;
    this.$emit('mounted');
    this.getFlights(this.localFormData)
      .then((flights => {
        if (this.localFormData.flightNumber === '' || this.localFormData.flightNumber === undefined) return;
        const foundFlight = flights.find(
          (flight) => flight.flightNumber === this.localFormData.flightNumber
        );
        if (!foundFlight) {
          this.updateSearch({
            ...this.localFormData,
            flightNumber: "",
          });
          return;
        }
        this.selectedFlight = foundFlight;
        this.getProducts();
      }));
  },
  methods: {
    onCancelChanges(): void {
      if (this.showDesktopBackdrop) {
        this.showDesktopBackdrop = false;
      }
      this.localEditMode = false;
    },

    updateSearch(criteria): void {
      this.$emit('update:updatingSearch', criteria);
      this.localEditMode = false;
    },

    updateLocalFormData(newData: Partial<TimeslotFormData>): void {
      this.localFormData = { ...this.localFormData, ...newData };
      this.$emit('update:formData', this.localFormData);
    },

    onDepartureDateChange(payload: TimeslotFormData): void {
      if (!payload.departureDate) return;

      const hasNoExistingDate = this.formData.departureDate === undefined;
      const hasPreviouslyFetched = this.lastFetchedFlightQuery !== undefined;

      // we only want to get products here on change, not on mount:
      //  this ensures we set the selected flight correctly on mount,
      //  whilst not incorrectly overriding it on updating the date
      if (hasNoExistingDate || hasPreviouslyFetched)
        this.getFlights(payload);
    },

    getFlights(payload: TimeslotFormData): Promise<FlightInfo> {
      // do not make the call without required information
      if (!payload.departureDate) return Promise.resolve([]);

      // do not request again if we already have data for the given date
      if (payload.departureDate === this.lastFetchedFlightQuery?.departureDate)
        return Promise.resolve(this.flights);

      this.flights = [];
      this.flightsRequestStatus = 'loading';
      this.lastFetchedFlightQuery = { ...payload };

      const searchHelper = searchPusherHelper();

      // so we can handle past date validation errors
      const hasRequestDateValidationFailed = (error: unknown) =>
        isPusherErrorWithResponse(error) && (
          // the API responds with errors here to indicate the date criterion was invalid
          'criteria.fasttrack.date1' in error.data.body.errors
        );

      const form = {
        entryDate: payload.departureDate,
        entryTime: { name: '', value: '23:59' },
        customers: {
          adult: payload.passengers ? payload.passengers : '1',
        },
        meta: {
          step: 'step1',
        },
      };

      return searchHelper
        .searchProducts({ ...form }, PRODUCT_TYPES.FASTTRACK, this.$i18n.locale)
        .then((rawFlightData) => {
          const flights = extractFlights(rawFlightData);
          this.flights = flights;
          this.flightsRequestStatus = 'success';
          return flights;
        })
        .catch((error: unknown) => {
          if (hasRequestDateValidationFailed(error)) {
            this.updateSearch({
              ...this.localFormData,
              flightNumber: "",
              departureDate: "",
            });
            return;
          }
          this.flightsRequestStatus = 'error';
          console.log(error);
          datadogRum.addError(error);
          this.showTechnicalIssueModal();
          return [];
        });
    },

    getProducts(): void {
      const isSameQuery =
        this.localFormData.departureDate === this.lastFetchedProductQuery?.departureDate
        && this.localFormData.flightNumber === this.lastFetchedProductQuery?.flightNumber
        && this.localFormData.passengers === this.lastFetchedProductQuery?.passengers;
      if (isSameQuery) return;

      // no flight selected => do not search or update
      if (this.selectedFlight.flightDetails === undefined) return;

      // selected flight does not have slots => can not search, set results to empty
      if (this.selectedFlight.eligibleQueues === undefined) {
        this.$emit('update:timeslotResults', []);
        return;
      }

      const slotOpening = new Date(this.selectedFlight.eligibleQueues.slotOpening);
      const slotClosing = new Date(this.selectedFlight.eligibleQueues.slotClosing);
      const form = {
        entryDate: format(slotOpening, DATE_TIME_FORMATS.year_month_day),
        entryTime: { name: '', value: format(slotOpening, DATE_TIME_FORMATS.time_24hr) },
        exitDate: format(slotClosing, DATE_TIME_FORMATS.year_month_day),
        exitTime: { name: '', value: format(slotClosing, DATE_TIME_FORMATS.time_24hr) },
        customers: {
          adult: this.localFormData.passengers,
        },
        flightDetails: this.selectedFlight.flightDetails,
        providerProductCode: this.selectedFlight.eligibleQueues.providerProductCode,
        meta: {
          step: 'step2',
        },
      } satisfies LoungeFormWithFlightDetails;

      this.lastFetchedProductQuery = { ...this.localFormData };

      // this is a possible known case where the request is rejected due to the slot
      // not being far enough in the future to allow bookings
      const hasRequestTimeValidationFailed = (error: unknown) =>
        isPusherErrorWithResponse(error) && (
          // the API responds with errors here to indicate the time criteria were invalid
          'criteria.fasttrack.time1' in error.data.body.errors
          || 'criteria.fasttrack.time2' in error.data.body.errors
        );

      const searchHelper = searchPusherHelper();
      searchHelper
        .searchProducts({ ...form }, APP_HERO_PRODUCTS.FASTTRACK, this.$i18n.locale)
        .then((searchProducts) => {
          this.$emit('update:timeslotResults', timeslotProductFilter(searchProducts));
        })
        .catch((error: unknown) => {
          if (hasRequestTimeValidationFailed(error)) {
            this.$emit('update:timeslotResults', []);
            return;
          }
          console.log(error);
          datadogRum.addError(error);
          this.showTechnicalIssueModal();
        });
    },

    onFlightChange(flight: FlightInfo): void {
      this.selectedFlight = flight;
    },

    showTechnicalIssueModal(): void {
      setTimeout(() => {
        this.$vueInstance.$openModal('GlobalModalError', {
          header: this.$vueInstance.$t('shared.modals.errors.preloaderError'),
          body: this.$vueInstance.$t('UI.modalError.tryAgainOrGoHome'),
          buttons: [
            {
              btnText: this.$vueInstance.$t('shared.buttons.close'),
            },
          ],
        });
      }, 500);
    },

    updateDesktopBackdrop(payload: boolean): void {
      this.showDesktopBackdrop = payload;
    },
  },
});
